import { wsStatus, AckMode } from "@/constants";
import { useI18n } from "@/contexts/I18nContext";
import { generateUuid } from "@/utils";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback,
} from "react";

interface WebSocketContextType {
  connect: (url: string, isReconnect?: boolean) => void;
  disconnect: () => void;
  sendMessage: (message: any) => Promise<any>;
  connectionStatus: number;
  lastMessage: any;
  error: string | null;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState(wsStatus.CLOSED);
  const { formatMessage } = useI18n();
  const wsRef = useRef<WebSocket | null>(null);

  // 重连机制相关状态
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3; //最大连接次数
  const reconnectInterval = 3000; // 重连延迟时间3秒

  // 清理函数
  const cleanup = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      //关闭连接
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
      wsRef.current = null;
      //将状态重置为下线
      setConnectionStatus(WebSocket.CLOSED);
    }
  }, []);

  // 重连逻辑
  const attemptReconnect = () => {
    //最多重连三次
    if (reconnectAttemptsRef.current < maxReconnectAttempts && wsRef.current) {
      const url = wsRef.current.url;
      //延迟3秒重连
      setTimeout(() => {
        console.log(
          `尝试重连 (${
            reconnectAttemptsRef.current + 1
          }/${maxReconnectAttempts})`
        );
        reconnectAttemptsRef.current += 1;
        connect(url, true);
      }, reconnectInterval);
    } else {
      setError(formatMessage("wsErrorText"));
    }
  };

  const connect = (url: string, isReconnect?: boolean) => {
    cleanup(); // 清理现有连接
    setError(null);
    setLastMessage(null);
    // 如果不是重连，那么重置重连次数
    if (!isReconnect) {
      reconnectAttemptsRef.current = 0;
    }
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket 连接成功");
      };

      ws.onmessage = (event) => {
        try {
          setError(null);
          const response = JSON.parse(event.data);
          // 处理ACK机制
          handleReceivedMessage(response);
          //服务器返回ready信息，才证明服务器已连接好
          if (response?.type === "ready") {
            //重连成功后重连次数重置为0
            reconnectAttemptsRef.current = 0;
            setConnectionStatus(ws.readyState);
          }
        } catch (err) {
          console.error("解析消息失败:", err);
        }
      };

      ws.onclose = (event) => {
        setConnectionStatus(ws.readyState);
        console.log("WebSocket 连接关闭", event.code, event.reason);
        switch (event.code) {
          case 4006: // 在其他地方上线
          case 4009: //服务器主动连接关闭
          case 4010: // 长时间没有发送消息
            //提示连接已关闭
            console.log(event.reason, "event.reason");
            // setError(event.reason || "当前对话已结束");
            //连接关闭后清除ws
            break;
          default:
            // 其他状态码需要重新发起连接，延迟3秒执行，最多3次，不成功则提示异常
            setError(formatMessage("abnormalDisconnectionText"));
            attemptReconnect();
        }
      };

      ws.onerror = () => {
        // 出现异常也需要重新发起连接，延迟3秒执行，最多3次，不成功则提示异常
        setError(formatMessage("abnormalDisconnectionText"));
        attemptReconnect();
        setConnectionStatus(ws.readyState);
      };
    } catch (err) {
      setConnectionStatus(wsStatus.CLOSED);
      console.error(err);
      // const errorMessage = (err as Error)?.message || "连接失败";
      // setError(errorMessage);
    }
  };

  // 处理接收到的消息
  const handleReceivedMessage = (response: any) => {
    // 处理ACK机制
    if (
      response.ack_mode === AckMode.ACK_REQUIRED &&
      wsRef.current?.readyState === WebSocket.OPEN
    ) {
      // 发送确认回执
      const confirmMessage = { ...response, ack_mode: AckMode.ACK_CONFIRMED };
      wsRef.current.send(JSON.stringify(confirmMessage));
    }
    // 设置最后一条消息
    setLastMessage(response);
  };

  //关闭连接
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, "用户主动断开连接");
    }
    cleanup();
  }, [cleanup]);

  //发送消息
  const sendMessage = (message: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (wsRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          // 确保消息有ID
          const messageWithData = {
            ...message,
            id: message.id || generateUuid(),
          };
          const messageString = JSON.stringify(messageWithData);
          wsRef.current.send(messageString);
          resolve(messageString);
        } catch (err) {
          console.error("发送消息失败:", err);
          reject(err);
        }
      }
    });
  };

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const value = {
    connect,
    disconnect,
    sendMessage,
    connectionStatus,
    lastMessage,
    error,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    // 返回默认实现避免调用时出错
    return {
      connect: () => {},
      disconnect: () => {},
      sendMessage: () => Promise.reject(new Error("发送消息失败")),
      connectionStatus: "uninitialized" as const,
      lastMessage: null,
      error: null,
    };
  }
  return context;
};
